// DocTemplatesExplorerPage — left-sidebar folder explorer for document templates.
// Mounted under `/documents/templates`. Selection lives on the URL path:
//
//   /documents/templates/<templatePath>
//   /documents/templates/f/<folderPath>
//
// This page is the single owner of which mutation runs and which dialog opens;
// the sidebar and preview pane stay presentational and route everything back
// through `handleAction` / callbacks.

import { useEffect, useMemo, useRef, useState } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  isDemoMode,
  useCopyDocTemplateFolderMutation,
  useCopyDocTemplateMutation,
  useCreateDocTemplateFolderMutation,
  useDeleteDocTemplateFolderByPathMutation,
  useDeleteDocTemplateFolderMutation,
  useDeleteDocTemplateMutation,
  useListDocTemplateFoldersQuery,
  useListDocTemplatesQuery,
  useMoveDocTemplateFolderMutation,
  useRenameDocTemplateFolderMutation,
  useRenameDocTemplateMutation,
  useUploadDocTemplateMutation,
  type DocTemplateFolder
} from '@axilon-apps/domains-ontology-ui-state'

import {
  ActionErrorDialog,
  countPrefixDescendants,
  DeleteItemDialog,
  expandAncestors,
  hasTextSelection,
  isPathPrefix,
  joinChildPath,
  joinUnderParent,
  leafSegment,
  MoveItemDialog,
  NameDialog,
  parentPathOf,
  PASTE_REFUSED,
  readMutationError,
  relocateUnderPrefix,
  relocatedCreatedPaths,
  RenameItemDialog,
  resolvePasteTarget,
  resolveUndo,
  subtreeFolderPaths,
  useExplorerClipboard,
  useExplorerUndo,
  type MoveItemKind,
  type RenameItemKind
} from '../../components/explorer'
import { useChatAppContext } from '../../chat-app-context'
import type { DocumentsOutletContext } from './DocumentsHomePage'
import DocTemplatePreviewPane from './DocTemplatePreviewPane'
import DocTemplatesExplorerSidebar, {
  type DocTemplateItem,
  type DocTemplatesSidebarAction
} from './DocTemplatesExplorerSidebar'
import DocTemplateUploadDialog, { type UploadSubmit } from './DocTemplateUploadDialog'
import { DOC_TEMPLATES_HOME, docTemplateFolderHref, docTemplateHref } from './explorer-paths'
import {
  buildDocTemplateAppContextEntries,
  DOC_TEMPLATES_SUPERSEDED_PARAMS
} from './doc-templates-app-context'
import { useDocTemplateExplorerSelection } from './useExplorerSelection'

interface RenameState {
  kind: RenameItemKind
  path: string
  name: string
  folder?: DocTemplateFolder
  item?: DocTemplateItem
}
interface DeleteState {
  kind: 'folder' | 'item'
  path: string
  name: string
  folder?: DocTemplateFolder
  item?: DocTemplateItem
}
interface MoveState {
  kind: MoveItemKind
  path: string
  name: string
  item?: DocTemplateItem
}

const DocTemplatesExplorerPage = () => {
  const navigate = useNavigate()
  const { tabsSlot } = useOutletContext<DocumentsOutletContext>()
  const [searchParams, setSearchParams] = useSearchParams()
  const routeSelection = useDocTemplateExplorerSelection()

  const templateFolders = useListDocTemplateFoldersQuery()
  const templates = useListDocTemplatesQuery()

  const [uploadTemplate] = useUploadDocTemplateMutation()
  const [deleteTemplate] = useDeleteDocTemplateMutation()
  const [renameTemplate] = useRenameDocTemplateMutation()
  const [copyTemplate] = useCopyDocTemplateMutation()
  const [createTemplateFolder] = useCreateDocTemplateFolderMutation()
  const [deleteTemplateFolder] = useDeleteDocTemplateFolderMutation()
  const [deleteTemplateFolderByPath] = useDeleteDocTemplateFolderByPathMutation()
  const [renameTemplateFolder] = useRenameDocTemplateFolderMutation()
  const [moveTemplateFolder] = useMoveDocTemplateFolderMutation()
  const [copyTemplateFolder] = useCopyDocTemplateFolderMutation()

  const folders = useMemo<DocTemplateFolder[]>(
    () => templateFolders.data ?? [],
    [templateFolders.data]
  )
  const items = useMemo<DocTemplateItem[]>(() => templates.data ?? [], [templates.data])
  const isLoading = templateFolders.isLoading || templates.isLoading

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [filter, setFilter] = useState('')
  const [uploadDialog, setUploadDialog] = useState<{ parentPath: string } | null>(null)
  const [createFolderDialog, setCreateFolderDialog] = useState<{ parentPath: string } | null>(null)
  const [renameDialog, setRenameDialog] = useState<RenameState | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<DeleteState | null>(null)
  const [moveDialog, setMoveDialog] = useState<MoveState | null>(null)
  const [actionError, setActionError] = useState<{ title: string; message: string } | null>(null)
  const clipboard = useExplorerClipboard()
  const undo = useExplorerUndo()
  const pasteInFlight = useRef(false)
  const undoInFlight = useRef(false)
  const canMutate = !isDemoMode()

  // Legacy `?file=<uuid>` / `?folder=<path>` → path URLs.
  useEffect(() => {
    if (isLoading) return
    const legacyFileId = searchParams.get('file')
    const legacyFolder = searchParams.get('folder')
    if (legacyFileId === null && legacyFolder === null) return

    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('file')
    nextParams.delete('folder')
    nextParams.delete('view')
    const search = nextParams.toString()
    const suffix = search === '' ? '' : `?${search}`

    if (legacyFileId) {
      const found = items.find((item) => item.id === legacyFileId)
      if (found) {
        navigate(`${docTemplateHref(found.path)}${suffix}`, { replace: true })
        return
      }
      setSearchParams(nextParams, { replace: true })
      return
    }

    if (legacyFolder) {
      navigate(`${docTemplateFolderHref(legacyFolder)}${suffix}`, { replace: true })
      return
    }

    setSearchParams(nextParams, { replace: true })
  }, [isLoading, searchParams, items, navigate, setSearchParams])

  const selectedItem = useMemo(() => {
    if (routeSelection.kind !== 'item') return null
    return items.find((item) => item.path === routeSelection.path) ?? null
  }, [routeSelection, items])

  const folderParam = routeSelection.kind === 'folder' ? routeSelection.path : ''

  useChatAppContext(
    buildDocTemplateAppContextEntries({
      item:
        selectedItem === null
          ? undefined
          : {
              name: selectedItem.name,
              path: selectedItem.path,
              id: selectedItem.id
            },
      folder: folderParam
    }),
    DOC_TEMPLATES_SUPERSEDED_PARAMS
  )

  const handleSelectItem = (id: string): void => {
    if (id === '') {
      navigate(DOC_TEMPLATES_HOME, { replace: true })
      return
    }
    const found = items.find((item) => item.id === id)
    if (!found) return
    navigate(docTemplateHref(found.path), { replace: true })
  }

  const handleSelectFolder = (path: string): void => {
    navigate(docTemplateFolderHref(path), { replace: true })
  }

  const handleToggle = (path: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  const defaultParentForHeaderCreate = useMemo(() => {
    if (folderParam) return folderParam
    if (selectedItem) return parentPathOf(selectedItem.path)
    return ''
  }, [folderParam, selectedItem])

  const itemHref = docTemplateHref
  const folderHref = docTemplateFolderHref
  const homeHref = DOC_TEMPLATES_HOME

  const goHomeIfUnder = (prefix: string): void => {
    if (folderParam && isPathPrefix(prefix, folderParam)) {
      navigate(homeHref, { replace: true })
    }
    if (selectedItem && isPathPrefix(prefix, selectedItem.path)) {
      navigate(homeHref, { replace: true })
    }
  }

  const afterFolderMoved = (fromPath: string, toPath: string): void => {
    setExpanded((prev) => expandAncestors(prev, toPath))
    if (folderParam && isPathPrefix(fromPath, folderParam)) {
      navigate(folderHref(relocateUnderPrefix(fromPath, toPath, folderParam)), { replace: true })
    }
    if (selectedItem && isPathPrefix(fromPath, selectedItem.path)) {
      navigate(itemHref(relocateUnderPrefix(fromPath, toPath, selectedItem.path)), {
        replace: true
      })
    }
  }

  const moveFolderToParent = async (
    fromPath: string,
    toParentPath: string,
    name?: string
  ): Promise<void> => {
    const toPath = joinUnderParent(toParentPath, leafSegment(fromPath))
    if (toPath === fromPath) return
    const displayName =
      name ?? folders.find((row) => row.path === fromPath)?.name ?? leafSegment(fromPath)
    const folderId = folders.find((row) => row.path === fromPath)?.id
    await moveTemplateFolder({
      fromPath,
      toPath,
      ...(name !== undefined ? { name } : {})
    }).unwrap()
    undo.push({
      op: 'folder-relocate',
      label: `Undo move of "${displayName}"`,
      ...(folderId !== undefined ? { folderId } : {}),
      atPath: toPath,
      toPath: fromPath,
      toName: displayName
    })
    afterFolderMoved(fromPath, toPath)
  }

  const moveItemToParent = async (item: DocTemplateItem, toParentPath: string): Promise<void> => {
    const toPath = joinUnderParent(toParentPath, leafSegment(item.path))
    if (toPath === item.path) return
    const updated = await renameTemplate({ id: item.id, input: { path: toPath } }).unwrap()
    undo.push({
      op: 'dashboard-relocate',
      label: `Undo move of "${item.name}"`,
      id: item.id,
      atPath: updated.path,
      toPath: item.path,
      toName: item.name
    })
    setExpanded((prev) => expandAncestors(prev, parentPathOf(updated.path)))
    navigate(itemHref(updated.path), { replace: true })
  }

  const handlePaste = async (destPath: string): Promise<void> => {
    if (!canMutate || pasteInFlight.current) return
    const result = resolvePasteTarget(clipboard.entry, destPath, folders, items)
    if (result.kind === 'noop') return
    if (result.kind === 'refused') {
      if (result.reason === PASTE_REFUSED.stale) clipboard.clear()
      setActionError({ title: "Couldn't paste", message: result.reason })
      return
    }
    pasteInFlight.current = true
    try {
      if (result.kind === 'move') {
        if (result.type === 'folder') {
          await moveFolderToParent(result.fromPath, result.toParentPath)
        } else {
          const item = items.find((row) => row.id === result.id)
          if (!item) {
            clipboard.clear()
            setActionError({ title: "Couldn't paste", message: PASTE_REFUSED.stale })
            return
          }
          await moveItemToParent(item, result.toParentPath)
        }
        clipboard.clear()
        return
      }
      if (result.type === 'folder') {
        const createdPaths = relocatedCreatedPaths(result.fromPath, result.toPath, folders, items)
        const copied = await copyTemplateFolder({
          fromPath: result.fromPath,
          toPath: result.toPath,
          name: result.name
        }).unwrap()
        undo.push({
          op: 'remove-folder',
          label: `Undo copy of "${result.name}"`,
          atPath: copied.toPath,
          createdPaths
        })
        setExpanded((prev) => expandAncestors(prev, copied.toPath))
        handleSelectFolder(copied.toPath)
        return
      }
      const copied = await copyTemplate({
        id: result.id,
        input: { name: result.name, path: result.path }
      }).unwrap()
      undo.push({
        op: 'remove-dashboard',
        label: `Undo copy of "${copied.name}"`,
        id: copied.id,
        atPath: copied.path,
        expectCode: ''
      })
      setExpanded((prev) => expandAncestors(prev, parentPathOf(copied.path)))
      navigate(itemHref(copied.path), { replace: true })
    } catch (err) {
      setActionError({ title: "Couldn't paste", message: readMutationError(err) })
    } finally {
      pasteInFlight.current = false
    }
  }
  const handlePasteRef = useRef(handlePaste)
  handlePasteRef.current = handlePaste

  const handleUndo = async (): Promise<void> => {
    if (!canMutate || undoInFlight.current) return
    while (true) {
      const entry = undo.pop()
      if (entry === undefined) return
      const result = resolveUndo(entry, folders, items)
      if (result.kind === 'stale') continue
      if (result.kind === 'blocked') {
        setActionError({ title: "Couldn't undo", message: result.reason })
        return
      }
      undoInFlight.current = true
      try {
        const applied = result.entry
        if (applied.op === 'folder-relocate') {
          if (
            applied.atPath === applied.toPath &&
            applied.folderId &&
            applied.toName !== undefined
          ) {
            await renameTemplateFolder({
              id: applied.folderId,
              input: { name: applied.toName }
            }).unwrap()
          } else {
            await moveTemplateFolder({
              fromPath: applied.atPath,
              toPath: applied.toPath,
              ...(applied.toName !== undefined ? { name: applied.toName } : {})
            }).unwrap()
            afterFolderMoved(applied.atPath, applied.toPath)
          }
          return
        }
        if (applied.op === 'dashboard-relocate') {
          const updated = await renameTemplate({
            id: applied.id,
            input: {
              path: applied.toPath,
              ...(applied.toName !== undefined ? { name: applied.toName } : {})
            }
          }).unwrap()
          setExpanded((prev) => expandAncestors(prev, parentPathOf(updated.path)))
          navigate(itemHref(updated.path), { replace: true })
          return
        }
        if (applied.op === 'remove-dashboard') {
          await deleteTemplate(applied.id).unwrap()
          if (selectedItem?.id === applied.id) navigate(homeHref, { replace: true })
          return
        }
        if (applied.op === 'remove-folder') {
          await deleteTemplateFolderByPath({ path: applied.atPath }).unwrap()
          goHomeIfUnder(applied.atPath)
          return
        }
        continue
      } catch (err) {
        setActionError({ title: "Couldn't undo", message: readMutationError(err) })
        return
      } finally {
        undoInFlight.current = false
      }
    }
  }
  const handleUndoRef = useRef(handleUndo)
  handleUndoRef.current = handleUndo

  const handleUpload = async (input: UploadSubmit): Promise<void> => {
    const created = await uploadTemplate({
      file: input.file,
      name: input.name,
      path: input.path,
      autoAddFields: input.autoAddFields
    }).unwrap()
    if (input.autoAddFields) {
      const added = created.aiFields ?? []
      if (created.aiError) {
        toast.error(`Uploaded “${input.name}”, but adding fields failed: ${created.aiError}`)
      } else if (added.length > 0) {
        toast.success(
          `Added ${added.length} field${added.length === 1 ? '' : 's'} to “${input.name}”`
        )
      } else {
        toast.warning(`Uploaded “${input.name}”, but the AI found no fillable spots.`)
      }
    } else {
      toast.success(`Uploaded template “${input.name}”`)
    }
    setExpanded((prev) => expandAncestors(prev, input.path))
    handleSelectItem(created.id)
    undo.push({
      op: 'remove-dashboard',
      label: `Undo upload of "${created.name}"`,
      id: created.id,
      atPath: created.path,
      expectCode: ''
    })
  }

  const handleCreateFolder = async (name: string): Promise<void> => {
    if (!createFolderDialog) return
    const path = joinChildPath(createFolderDialog.parentPath, name)
    if (path === '') throw new Error('Name must contain at least one letter, digit, dash, or dot.')
    const created = await createTemplateFolder({ name, path }).unwrap()
    undo.push({
      op: 'remove-folder',
      label: `Undo create of "${name}"`,
      atPath: created.path,
      createdPaths: [created.path]
    })
    setExpanded((prev) => expandAncestors(prev, created.path))
    handleSelectFolder(created.path)
    toast.success(`Created folder “${name}”`)
  }

  const handleRename = async ({ name, path }: { name: string; path?: string }): Promise<void> => {
    if (!renameDialog) return
    if (renameDialog.kind === 'folder') {
      const fromPath = renameDialog.path
      const toPath = path ?? fromPath
      if (renameDialog.folder?.id && toPath === fromPath) {
        await renameTemplateFolder({ id: renameDialog.folder.id, input: { name } }).unwrap()
        undo.push({
          op: 'folder-relocate',
          label: `Undo rename of "${renameDialog.name}"`,
          folderId: renameDialog.folder.id,
          atPath: fromPath,
          toPath: fromPath,
          toName: renameDialog.name
        })
        toast.success(`Renamed to “${name}”`)
        return
      }
      if (toPath !== fromPath) {
        await moveTemplateFolder({ fromPath, toPath, name }).unwrap()
        undo.push({
          op: 'folder-relocate',
          label: `Undo rename of "${renameDialog.name}"`,
          ...(renameDialog.folder?.id !== undefined ? { folderId: renameDialog.folder.id } : {}),
          atPath: toPath,
          toPath: fromPath,
          toName: renameDialog.name
        })
        afterFolderMoved(fromPath, toPath)
      }
      toast.success(`Renamed to “${name}”`)
      return
    }
    if (renameDialog.item) {
      const input = path !== undefined ? { name, path } : { name }
      const updated = await renameTemplate({ id: renameDialog.item.id, input }).unwrap()
      undo.push({
        op: 'dashboard-relocate',
        label: `Undo rename of "${renameDialog.name}"`,
        id: renameDialog.item.id,
        atPath: updated.path,
        toPath: renameDialog.path,
        toName: renameDialog.name
      })
      if (updated.path !== renameDialog.path) {
        setExpanded((prev) => expandAncestors(prev, parentPathOf(updated.path)))
        navigate(itemHref(updated.path), { replace: true })
      }
      toast.success(`Renamed to “${name}”`)
    }
  }

  // Deletes are terminal here: report and template bytes live in object
  // storage and are gone once the row is removed, so there is nothing to
  // restore. Clearing the stack keeps a later undo from replaying an older
  // entry against a tree that no longer matches it.
  const handleDelete = async (): Promise<void> => {
    if (!deleteDialog) return
    if (deleteDialog.kind === 'folder') {
      if (deleteDialog.folder?.id) {
        await deleteTemplateFolder(deleteDialog.folder.id).unwrap()
      } else {
        await deleteTemplateFolderByPath({ path: deleteDialog.path }).unwrap()
      }
      undo.clear()
      goHomeIfUnder(deleteDialog.path)
      toast.success(`Deleted folder “${deleteDialog.name}”`)
      return
    }
    if (deleteDialog.item) {
      const item = deleteDialog.item
      await deleteTemplate(item.id).unwrap()
      undo.clear()
      if (selectedItem?.id === item.id) navigate(homeHref, { replace: true })
      toast.success(`Deleted “${item.name}”`)
    }
  }

  const handleMove = async (destinationFolderPath: string): Promise<void> => {
    if (!moveDialog) return
    if (moveDialog.kind === 'folder') {
      await moveFolderToParent(moveDialog.path, destinationFolderPath)
      return
    }
    if (moveDialog.item) {
      await moveItemToParent(moveDialog.item, destinationFolderPath)
    }
  }

  const handleAction = (action: DocTemplatesSidebarAction): void => {
    switch (action.kind) {
      case 'upload':
        setUploadDialog({ parentPath: action.parentPath })
        return
      case 'create-folder':
        setCreateFolderDialog({ parentPath: action.parentPath })
        return
      case 'rename-folder':
        setRenameDialog({
          kind: 'folder',
          path: action.path,
          name: action.name,
          folder: action.folder
        })
        return
      case 'delete-folder':
        setDeleteDialog({
          kind: 'folder',
          path: action.path,
          name: action.name,
          folder: action.folder
        })
        return
      case 'move-folder':
        setMoveDialog({ kind: 'folder', path: action.path, name: action.name })
        return
      case 'move-item':
        setMoveDialog({
          kind: 'template',
          path: action.item.path,
          name: action.item.name,
          item: action.item
        })
        return
      case 'drop-folder':
        void moveFolderToParent(action.fromPath, action.toParentPath).catch((err) => {
          setActionError({ title: "Couldn't move", message: readMutationError(err) })
        })
        return
      case 'drop-item':
        void moveItemToParent(action.item, action.toParentPath).catch((err) => {
          setActionError({ title: "Couldn't move", message: readMutationError(err) })
        })
        return
      case 'copy-folder':
        clipboard.copy({ kind: 'folder', path: action.path, name: action.name })
        return
      case 'cut-folder':
        clipboard.cut({ kind: 'folder', path: action.path, name: action.name })
        return
      case 'copy-item':
        clipboard.copy({
          kind: 'item',
          id: action.item.id,
          path: action.item.path,
          name: action.item.name
        })
        return
      case 'cut-item':
        clipboard.cut({
          kind: 'item',
          id: action.item.id,
          path: action.item.path,
          name: action.item.name
        })
        return
      case 'paste':
        void handlePaste(action.parentPath)
        return
      case 'undo':
        void handleUndo()
        return
      case 'rename-item':
        setRenameDialog({
          kind: 'template',
          path: action.item.path,
          name: action.item.name,
          item: action.item
        })
        return
      case 'delete-item':
        setDeleteDialog({
          kind: 'item',
          path: action.item.path,
          name: action.item.name,
          item: action.item
        })
        return
    }
  }

  const dialogOpen =
    uploadDialog !== null ||
    createFolderDialog !== null ||
    renameDialog !== null ||
    deleteDialog !== null ||
    moveDialog !== null ||
    actionError !== null

  useEffect(() => {
    if (!canMutate) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (dialogOpen) return
      const target = event.target
      if (target instanceof HTMLElement) {
        const tag = target.tagName
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) {
          return
        }
      }

      const mod = event.metaKey || event.ctrlKey
      if (mod && !event.altKey && !event.shiftKey) {
        const key = event.key.toLowerCase()
        if (key === 'c') {
          if (hasTextSelection()) return
          if (folderParam) {
            event.preventDefault()
            const folder = folders.find((row) => row.path === folderParam)
            clipboard.copy({
              kind: 'folder',
              path: folderParam,
              name: folder?.name ?? leafSegment(folderParam)
            })
          } else if (selectedItem) {
            event.preventDefault()
            clipboard.copy({
              kind: 'item',
              id: selectedItem.id,
              path: selectedItem.path,
              name: selectedItem.name
            })
          }
          return
        }
        if (key === 'x') {
          if (folderParam) {
            event.preventDefault()
            const folder = folders.find((row) => row.path === folderParam)
            clipboard.cut({
              kind: 'folder',
              path: folderParam,
              name: folder?.name ?? leafSegment(folderParam)
            })
          } else if (selectedItem) {
            event.preventDefault()
            clipboard.cut({
              kind: 'item',
              id: selectedItem.id,
              path: selectedItem.path,
              name: selectedItem.name
            })
          }
          return
        }
        if (key === 'v') {
          if (clipboard.entry === null) return
          event.preventDefault()
          void handlePasteRef.current(defaultParentForHeaderCreate)
          return
        }
        if (key === 'z') {
          if (undo.entries.length === 0) return
          event.preventDefault()
          void handleUndoRef.current()
          return
        }
      }

      if (event.key === 'Escape' && clipboard.entry?.mode === 'cut') {
        event.preventDefault()
        clipboard.clear()
        return
      }

      if (folderParam) {
        const folder = folders.find((row) => row.path === folderParam)
        const name = folder?.name ?? leafSegment(folderParam)
        if (event.key === 'F2') {
          event.preventDefault()
          setRenameDialog({ kind: 'folder', path: folderParam, name, folder })
          return
        }
        if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault()
          setDeleteDialog({ kind: 'folder', path: folderParam, name, folder })
        }
        return
      }
      if (selectedItem) {
        if (event.key === 'F2') {
          event.preventDefault()
          setRenameDialog({
            kind: 'template',
            path: selectedItem.path,
            name: selectedItem.name,
            item: selectedItem
          })
          return
        }
        if (event.key === 'Delete' || event.key === 'Backspace') {
          event.preventDefault()
          setDeleteDialog({
            kind: 'item',
            path: selectedItem.path,
            name: selectedItem.name,
            item: selectedItem
          })
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    canMutate,
    clipboard,
    defaultParentForHeaderCreate,
    dialogOpen,
    folderParam,
    folders,
    selectedItem,
    undo.entries.length
  ])

  const moveDisabledPaths = useMemo(() => {
    const blocked = new Set<string>()
    if (moveDialog?.kind === 'folder') {
      for (const path of subtreeFolderPaths(moveDialog.path, folders, items)) {
        blocked.add(path)
      }
    }
    return blocked
  }, [moveDialog, folders, items])

  const deleteDescendantCount =
    deleteDialog?.kind === 'folder' ? countPrefixDescendants(deleteDialog.path, folders, items) : 0

  return (
    /* [contain:size] keeps the explorer from inflating AppLayout's outlet
       wrapper (same Docs/Tags pattern): without it, min-height:auto grows
       with the preview and the collapse handle centers on the whole page. */
    <div className="flex h-full min-h-0 flex-col overflow-hidden [contain:size]">
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <DocTemplatesExplorerSidebar
          folders={folders}
          items={items}
          selectedId={selectedItem?.id ?? null}
          selectedFolder={folderParam || null}
          expanded={expanded}
          onToggle={handleToggle}
          onSelectItem={handleSelectItem}
          onSelectFolder={handleSelectFolder}
          onAction={handleAction}
          filter={filter}
          onFilterChange={setFilter}
          isLoading={isLoading}
          topSlot={tabsSlot}
          createParentPath={defaultParentForHeaderCreate}
          clipboard={clipboard.entry}
          undoLabel={undo.entries.at(-1)?.label ?? null}
        />

        <div className="relative z-0 min-w-0 flex-1">
          {isLoading && !selectedItem ? (
            <div className="flex items-center gap-2 p-6 text-sm text-ink-3">
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Loading templates…
            </div>
          ) : selectedItem ? (
            <DocTemplatePreviewPane
              item={selectedItem}
              onDelete={(item) =>
                setDeleteDialog({
                  kind: 'item',
                  path: item.path,
                  name: item.name,
                  item
                })
              }
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
              <FileText className="size-8 text-ink-3" aria-hidden />
              <p className="m-0 max-w-sm text-sm text-ink-3">
                {items.length === 0
                  ? 'No templates yet. Upload one from the sidebar.'
                  : 'Select a template from the sidebar to preview it.'}
              </p>
            </div>
          )}
        </div>
      </div>

      <DocTemplateUploadDialog
        open={uploadDialog !== null}
        folders={folders}
        items={items}
        defaultParentPath={uploadDialog?.parentPath ?? ''}
        onClose={() => setUploadDialog(null)}
        onSubmit={handleUpload}
      />

      <NameDialog
        open={createFolderDialog !== null}
        title="New folder"
        label="Folder name"
        placeholder="quarterly"
        submitLabel="Create folder"
        onClose={() => setCreateFolderDialog(null)}
        onSubmit={handleCreateFolder}
      />

      <RenameItemDialog
        open={renameDialog !== null}
        kind={renameDialog?.kind ?? 'folder'}
        currentName={renameDialog?.name ?? ''}
        path={renameDialog?.path ?? ''}
        rewriteLeafPath
        onClose={() => setRenameDialog(null)}
        onSubmit={handleRename}
      />

      <DeleteItemDialog
        open={deleteDialog !== null}
        kind={deleteDialog?.kind === 'folder' ? 'folder' : 'template'}
        name={deleteDialog?.name ?? ''}
        path={deleteDialog?.path ?? ''}
        descendantCount={deleteDescendantCount}
        recursive
        itemNote="and remove its stored file"
        onClose={() => setDeleteDialog(null)}
        onConfirm={handleDelete}
      />

      <MoveItemDialog
        open={moveDialog !== null}
        kind={moveDialog?.kind ?? 'folder'}
        name={moveDialog?.name ?? ''}
        currentPath={moveDialog?.path ?? ''}
        folders={folders}
        dashboards={items}
        disabledPaths={moveDisabledPaths}
        onClose={() => setMoveDialog(null)}
        onSubmit={handleMove}
      />

      <ActionErrorDialog
        open={actionError !== null}
        title={actionError?.title}
        message={actionError?.message ?? ''}
        onClose={() => setActionError(null)}
      />
    </div>
  )
}

export default DocTemplatesExplorerPage
